import React, {createContext, useContext, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useCommerceAPI} from '../commerce-api/contexts'
import ProductRequestMerger from '../utils/product-request-merger'

const CachedProductsContext = createContext()

/**
 * Provider for in-memory cached products over api.shopperProducts.getProducts.
 *
 * @see useCachedProducts
 */
export const CachedProductsProvider = ({children}) => {
    const api = useCommerceAPI()
    const ctx = useMemo(() => {
        // Map of extraParams to {cache, queue} - use to separate cache and queue per extraParams
        const perExtraParamsCacheAndQueue = new Map()
        /**
         * Get existing cache and queue for given extraParams or create new ones.
         * @param {Object} extraParams
         * @returns {ProductRequestMerger}
         */
        const getCacheAndQueue = (extraParams) => {
            // Sort extraParams to make sure that the same extraParams will always be represented by the same key
            const key = JSON.stringify(
                Object.entries(extraParams).sort(([a], [b]) => a.localeCompare(b))
            )

            if (!perExtraParamsCacheAndQueue.has(key)) {
                const queue = new ProductRequestMerger({
                    cache: true,
                    getProducts: api.shopperProducts.getProducts,
                    extraParams
                })

                perExtraParamsCacheAndQueue.set(key, queue)
            }

            return perExtraParamsCacheAndQueue.get(key)
        }

        const fetch = async ({parameters: {ids, ...extraParams}}) => {
            const queue = getCacheAndQueue(extraParams)
            return {
                data: await queue.getProducts(ids.split(','))
            }
        }

        return fetch
    }, [api])

    return <CachedProductsContext.Provider value={ctx}>{children}</CachedProductsContext.Provider>
}

CachedProductsProvider.propTypes = {
    children: PropTypes.node.isRequired
}

/**
 * Hook to get function to obtain products from api.shopperProducts.getProducts that caches the results in memory.
 *
 * @returns {function({parameters: {ids: string}}): Promise<{data: Array}>}
 */
const useCachedProducts = () => {
    const api = useCommerceAPI()
    const context = useContext(CachedProductsContext)

    if (!context) {
        return api.shopperProducts.getProducts
    }

    return context
}

export default useCachedProducts
